import React, { useEffect, useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { Box } from '@mui/material'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

import { B2CPaymentsRequest } from '@/@types/b2c.types.ts'
import { TableComponent } from '@/components/table'
import { useGetB2CRegistryOperations } from '@/services'
import { useIsMobile } from '@/hooks'
import { APP_ROUTES } from '@/constants.ts'
import { FetchErrorMessage } from '@/components'
import { DefaultFiltersState } from '@/stores'
import {
  defaultSettings,
  getColumnsSettings,
  KEY_PAGE,
  PartialPaymentsRequest,
} from '@/pages/b2c/default-settings-payments.tsx'
import { useAppDispatch, useAppSelector } from '@/stores/hooks.ts'
import { userSelectors } from '@/stores/slices/user-slice.ts'
import { b2cRegistryActions, useB2CRegistryState } from '@/stores/slices/b2c-registry-slice.ts'

const columnHelper = createColumnHelper<B2CPaymentsRequest>()
const defaultColumns = getColumnsSettings(columnHelper)

export const B2CRegistryRequestTable = (): React.ReactElement => {
  const { id = '' } = useParams<{ id: string }>()
  const { isLoading, data, isError, refetch, isFetching } = useGetB2CRegistryOperations(id)
  const navigate = useNavigate()
  const settings = useAppSelector((state) => userSelectors.personalSettings(state, KEY_PAGE))
  const { page, showCount } = useB2CRegistryState()
  const isMobile = useIsMobile()
  const dispatch = useAppDispatch()

  const b2cRequestCols = useMemo(() => {
    const format = isMobile ? 'mobile' : 'desktop'

    const desktopSettings = (settings?.[format] ?? Object.keys(defaultSettings)) as Array<keyof PartialPaymentsRequest>

    const dynamicSettings = desktopSettings.map((e) => defaultColumns[e])
    return [...dynamicSettings]
  }, [settings, isMobile])

  useEffect(() => {
    return () => {
      dispatch(b2cRegistryActions.resetAllFilters())
    }
  }, [])

  const handleViewTransaction = (row: B2CPaymentsRequest) => () => {
    navigate(generatePath(APP_ROUTES.B2CView, { id: row.transferId.toString() }))
  }

  const handlePaginationParam = (param: keyof DefaultFiltersState) => (value: number) =>
    dispatch(
      b2cRegistryActions.setFilter({
        filterKey: param,
        filterValue: value,
      })
    )

  return (
    <FetchErrorMessage isError={isError} refetch={refetch} isFetching={isFetching}>
      <Box mt={3}>
        <TableComponent<B2CPaymentsRequest>
          rowKey="transferId"
          isLoading={isLoading || isFetching}
          columns={b2cRequestCols}
          page={page}
          size={showCount}
          totalPages={data?.data.totalPages}
          data={data?.data?.content ?? []}
          onPageChange={handlePaginationParam('page')}
          onSizeChange={handlePaginationParam('showCount')}
          onRowClick={handleViewTransaction}
        />
      </Box>
    </FetchErrorMessage>
  )
}
